import { Dialog, DialogPanel } from '@headlessui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import DeleteIcon from '~/shared/assets/icons/delete.svg';
import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/ButtonsV2';
import { useXFetch } from '~/shared/utils/useXFetch';
import { toaster } from '~/shared/utils/toaster';

import type { AutoscaleRule, AutoscaleRulesResponse } from '../hooks/useAutoscaleRulesQuery';

import { buildDeleteRuleUrl } from './hooks/buildDeleteRuleUrl';
import { removeRuleFromResponse } from './hooks/removeRuleFromResponse';

interface DeleteRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  rule: AutoscaleRule;
  onDeleteSuccess?: () => void;
}

export default function DeleteRuleModal({
  isOpen,
  onClose,
  rule,
  onDeleteSuccess,
}: DeleteRuleModalProps) {
  const api = useXFetch();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteRule, isPending } = useMutation({
    mutationKey: ['delete-rule', rule.ruleId],
    mutationFn: async () => {
      await api(buildDeleteRuleUrl(rule), {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      toaster({
        variant: 'success',
        title: 'Rule Deleted',
        message: `Rule ${rule.ruleId} has been successfully deleted.`,
        toastId: rule.ruleId,
      });
      queryClient.setQueriesData<AutoscaleRulesResponse>({ queryKey: ['autoscaleRules'] }, (old) =>
        removeRuleFromResponse(old, rule)
      );
      queryClient.invalidateQueries({ queryKey: ['autoscaleRules'] });
      onClose();
      onDeleteSuccess?.();
    },
    onError: (error) => {
      console.error('Failed to delete rule:', error);
      toaster({
        variant: 'error',
        title: 'Deletion Error',
        message: `Rule ${rule.ruleId} failed to delete. Please try again.`,
        toastId: rule.ruleId,
      });
    },
  });

  const handleDelete = async () => {
    await deleteRule();
  };

  const handleClose = () => {
    if (!isPending) {
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      as="div"
      className="relative z-[99] focus:outline-none auto-scale-rule-dialog"
      onClose={handleClose}
    >
      <div
        className="fixed inset-0 p-4 w-screen overflow-y-auto bg-[#00000059] backdrop-blur-[6px] auto-scale-rule-dialog-backdrop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center h-full p-4">
          <DialogPanel
            transition
            className="w-full max-w-[689px] rounded-xl bg-white p-6 backdrop-blur-2xl duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="flex flex-col w-full items-center">
              <DeleteIcon className="size-8 text-red" />
              <Typography variant="H3-med" fontWeight={600} className="mt-6 font-family-outfit">
                Are you sure you want to delete this rule?
              </Typography>
              <Typography variant="p3-reg" className="mt-2 max-w-[536px] text-center">
                This rule will be permanently deleted.Volumes already autoscaled by this rule will
                continue autoscaling.
              </Typography>

              <div className="flex gap-3 mt-6 pt-6">
                <Button
                  variant="third"
                  className="w-[163px] h-[44px] flex gap-2 items-center justify-center"
                  onClick={handleDelete}
                  disabled={isPending}
                  leftIcon={<DeleteIcon className="size-6 " />}
                >
                  <Typography variant="buttons-m" color="text-primary-900">
                    {isPending ? 'Deleting...' : 'Yes, delete'}
                  </Typography>
                </Button>
                <Button className="w-[152px]" onClick={handleClose} disabled={isPending}>
                  No, cancel
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
